import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

// Tom-select parallel to hierarchy_select_controller.js. Consumes the same
// nested JSON produced by the scoped Select2::*TeamHierarchiesController
// endpoints, flattens it into tom-select's optgroup + option model with a
// per-option `depth` used for visual indentation, and dispatches the shared
// `bk360-team-member-select-changed` event on selection changes so existing
// URL-update listeners keep working.
const ROLE_LABELS = {
  team_manager: "Team Manager",
  pod_leader: "Pod Leader",
  pod_employee: "Pod Employee",
}
const DEPTH_BY_ROLE = { team_manager: 0, pod_leader: 1, pod_employee: 2 }

export default class extends Controller {
  static values = {
    url: String,
    submitOnChange: { type: Boolean, default: true },
    containerSelector: String,
  }

  connect() {
    this.initTomSelect()
    this.carryPreselectedMetadata()
  }

  disconnect() {
    if (this.select) this.select.destroy()
  }

  initTomSelect() {
    this.select = new TomSelect(this.element, {
      plugins: ["remove_button"],
      valueField: "value",
      labelField: "text",
      searchField: ["text"],
      optgroupField: "optgroup",
      optgroupLabelField: "label",
      optgroupValueField: "value",
      maxOptions: null,
      hideSelected: true,
      closeAfterSelect: false,
      allowEmptyOption: false,
      placeholder: this.element.getAttribute("placeholder"),
      load: (query, callback) => this.load(query, callback),
      render: {
        option: (item, escape) => this.renderOption(item, escape),
        item: (item, escape) => this.renderItem(item, escape),
        optgroup_header: (data, escape) => this.renderOptgroupHeader(data, escape),
      },
    })

    this.dropdownOpen = false
    this.pendingDispatch = false

    this.select.on("focus", () => {
      if (!this.select.control_input.value.length) this.select.load("")
    })
    this.select.on("dropdown_open", () => {
      this.dropdownOpen = true
    })
    this.select.on("dropdown_close", () => {
      this.dropdownOpen = false
      if (this.pendingDispatch) {
        this.pendingDispatch = false
        this.dispatchSelection()
      }
    })
    // Fires on add, remove, clear, and programmatic changes. Batch during
    // open dropdown (multi-select UX); otherwise dispatch immediately so
    // pill removals / backspace deletes update the URL without waiting.
    this.select.on("change", () => {
      if (this.dropdownOpen) {
        this.pendingDispatch = true
      } else {
        this.dispatchSelection()
      }
    })
  }

  // Preselected <option data-role-type=...> rows come in via the view component.
  // Tom-select doesn't read those extra attrs; copy them onto its option map so
  // the event dispatcher can bucket them into the right URL param.
  carryPreselectedMetadata() {
    this.element.querySelectorAll("option[data-role-type]").forEach((el) => {
      const item = this.select.options[el.value]
      if (!item) return
      item.role_type = el.getAttribute("data-role-type")
      item.depth = DEPTH_BY_ROLE[item.role_type] ?? 0
    })
  }

  load(query, callback) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", query)
    fetch(url)
      .then((response) => response.json())
      .then(({ options = [], optgroups = [] }) => {
        optgroups.forEach((g) => this.select.addOptionGroup(g.value, g))
        callback(options)
      })
      .catch(() => callback())
  }

  renderOption(item, escape) {
    const depth = item.depth ?? 0
    const pad = 12 + depth * 18
    const roleLabel = ROLE_LABELS[item.role_type] || ""
    const nameClass = `flex-grow-1 text-truncate pe-2${depth === 0 ? " fw-semibold" : ""}`
    return `<div class="d-flex align-items-center" style="padding-left: ${pad}px;">
      <span class="${nameClass}">${escape(item.text)}</span>
      <small class="text-muted flex-shrink-0">${escape(roleLabel)}</small>
    </div>`
  }

  renderItem(item, escape) {
    const roleLabel = ROLE_LABELS[item.role_type] || ""
    const suffix = roleLabel ? ` <small class="text-muted">(${escape(roleLabel)})</small>` : ""
    return `<div>${escape(item.text)}${suffix}</div>`
  }

  renderOptgroupHeader(data, escape) {
    return `<div class="optgroup-header fw-bold text-uppercase small text-muted px-2 py-1">${escape(data.label)}</div>`
  }

  dispatchSelection() {
    const buckets = { team_manager_ids: [], pod_leader_ids: [], pod_employee_ids: [] }
    const items = this.select.items || []
    items.forEach((id) => {
      const opt = this.select.options[id]
      if (!opt) return
      if (opt.role_type === "team_manager") buckets.team_manager_ids.push(id)
      else if (opt.role_type === "pod_leader") buckets.pod_leader_ids.push(id)
      else if (opt.role_type === "pod_employee") buckets.pod_employee_ids.push(id)
    })
    document.dispatchEvent(new CustomEvent("bk360-team-member-select-changed", {
      bubbles: true,
      detail: buckets,
    }))
    if (this.submitOnChangeValue) this.submitContainer()
  }

  submitContainer() {
    if (!this.hasContainerSelectorValue) return
    const el = document.querySelector(this.containerSelectorValue)
    const form = el?.closest("form")
    if (form) form.submit()
  }
}
